import type { Name } from '@lmc/ts-common'
import type { ToggleConfig } from '../../generic/teamCapabilities'
import {
  CapabilityKey,
  getCapabilityTogglesForRole
} from '../../generic/teamCapabilities'
import type { WizardData, WizardScope } from './types'

// `name: Name` so this payload is structurally assignable to `Partial<User>`
// from `useUser.createUser`. The Name type in @lmc/ts-common requires `full`,
// so we compute it client-side from first+last; backend may overwrite it on
// save.
//
// CM-3388: `home`/`homeGroup` aren't on the wizard's CreatePayload type today
// (and aren't part of the single-site flow), so they're appended via defensive
// casts at the call site. They're scope-driven:
//   specific-sites  → home: homes[0]      (legacy: createUser({ ...values, home: values.homes?.[0] }))
//   group-dashboard → homeGroup (viewer)  (legacy: createUser({ ...values, homeGroup: viewer.homeGroup }))
//   single-site     → neither             (unchanged TeamListV2 behaviour)
export type CreatePayload = {
  name: Name
  email: string
  jobTitle?: string
  role: WizardData['role']
  permissions: string[]
  medicationPermission: boolean
  // CM-3785: newly invited members should start active so they appear in the list
  active: boolean
  // TODO(CM-3388): `home`/`homeGroup` aren't yet typed on the create contract.
  home?: string
  homeGroup?: string
}

export type BuildCreatePayloadContext = {
  scope?: WizardScope
  // The viewer's homeGroup id from redux `state.global.user` — only consumed
  // for the 'group-dashboard' scope.
  viewerHomeGroup?: string
}

export const buildCreatePayload = (
  data: WizardData,
  context: BuildCreatePayloadContext = {}
): CreatePayload & { homes?: string[] } => {
  const { scope = 'single-site', viewerHomeGroup } = context
  const toggles = getCapabilityTogglesForRole(data.role)
  const permissions = toggles
    .filter(
      (t): t is ToggleConfig & { permissionId: string } =>
        Boolean(t.permissionId) && data.capabilities[t.key] === true
    )
    .map((t) => t.permissionId)

  const first = data.firstName.trim()
  const last = data.lastName.trim()
  const trimmedJobTitle = data.jobTitle.replace(/\s+/g, ' ').trim()
  const base: CreatePayload & { homes?: string[] } = {
    name: { first, last, full: `${first} ${last}`.trim() },
    email: data.email.trim(),
    role: data.role,
    permissions,
    medicationPermission:
      data.capabilities[CapabilityKey.MedicationManagement] === true,
    active: true
  }
  if (trimmedJobTitle) {
    base.jobTitle = trimmedJobTitle
  }

  // CM-3776: site-scoped users carry all selected home ids; group-dashboard users
  // carry the viewer's homeGroup. Legacy behavior (home: homes[0]) is updated to
  // support multiple site assignment.
  if (scope === 'specific-sites') {
    if (data.homes && data.homes.length > 0) {
      base.homes = data.homes
      // Keep backwards compatibility: also set home to first selected for clients
      // that don't understand the homes array yet
      base.home = data.homes[0]
    }
  } else if (scope === 'group-dashboard') {
    if (viewerHomeGroup) {
      base.homeGroup = viewerHomeGroup
    }
  }

  return base
}
